import { s, type SpacingToken } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';

type ThemeTypographyResolver = (theme: Theme) => string | number;

type TypographyTextSx = {
  fontSize?: ThemeTypographyResolver | string | number;
  lineHeight?: ThemeTypographyResolver | string | number;
  fontWeight?: number;
  textTransform?: string;
};

type RootTypographyTextSx = TypographyTextSx;

type TypographySelectorSx = Record<string, TypographyTextSx>;

type NestedTypographySelectorSx = {
  '&&': TypographyTextSx;
};

type SurfaceBackgroundSx = {
  backgroundColor: string;
};

const resolveSubtitle1FontSize = (theme: Theme): string | number =>
  theme.typography.subtitle1.fontSize ?? 'inherit';
const resolveSubtitle1LineHeight = (theme: Theme): string | number =>
  theme.typography.subtitle1.lineHeight ?? 'normal';
const resolveSubtitle2LineHeight = (theme: Theme): string | number =>
  theme.typography.subtitle2.lineHeight ?? 'normal';

export const settingsPageTypographySx: TypographySelectorSx = {
  '& .MuiTypography-body1:not(label)': {
    lineHeight: (theme: Theme) => resolveSubtitle1LineHeight(theme),
  },
};

export const settingsCopyTextSx: NestedTypographySelectorSx = {
  '&&': {
    lineHeight: (theme: Theme) => resolveSubtitle1LineHeight(theme),
  },
};

export const settingsValueTextSx = settingsCopyTextSx;

export const settingsFieldMeasureSx = {
  width: '100%',
  maxWidth: '360px',
} as const;

export const settingsWideFieldMeasureSx = {
  width: '100%',
  maxWidth: '360px',
} as const;

export const settingsMetadataLabelTextSx: SxProps<Theme> = [
  settingsCopyTextSx,
  {
    maxWidth: 'none',
    m: 0,
  },
];

export const settingsMetadataValueTextSx = settingsValueTextSx;

export const settingsMetadataCellSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const settingsMetadataRowSx: SxProps<Theme> = [
  {
    display: 'flex',
    flexWrap: { xs: 'wrap', sm: 'nowrap' },
    gap: { xs: s('sm'), sm: s('md') },
    width: '100%',
    alignItems: 'flex-end',
  },
  settingsWideFieldMeasureSx,
];

export const settingsMetadataKeyValueRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: s('md'),
  width: '100%',
};

export const settingsHelperTextSx: SxProps<Theme> = [
  settingsCopyTextSx,
  {
    maxWidth: '62ch',
    m: 0,
  },
];

export const settingsLabelTextSx: NestedTypographySelectorSx = {
  '&&': {
    fontSize: (theme: Theme) => resolveSubtitle1FontSize(theme),
    lineHeight: (theme: Theme) => resolveSubtitle1LineHeight(theme),
    fontWeight: 400,
    textTransform: 'none',
  },
};

export const planSettingsLimitsLabelTextSx: NestedTypographySelectorSx = {
  '&&': {
    lineHeight: (theme: Theme) => resolveSubtitle2LineHeight(theme),
  },
};

export const planSettingsLimitsHeadingTextSx: RootTypographyTextSx = {
  fontSize: (theme: Theme) => resolveSubtitle1FontSize(theme),
  lineHeight: (theme: Theme) => resolveSubtitle2LineHeight(theme),
};

export const settingsPanelSurfaceSx: SurfaceBackgroundSx = {
  backgroundColor: 'var(--surface-card)',
};

export const settingsInputBackgroundColor = 'var(--surface-page)' as const;

export const settingsPanelsGap: SpacingToken = '2xl';

export const settingsContentMaxWidth = '920px';
